import {
  all,
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  takeEvery,
} from "redux-saga/effects";

import { connectToDevice } from "actions";

import {
  CONNECT_TO_PLC,
  CONNECT_TO_SCALES,
  DISCONNECT_FROM_DEVICE,
  DEVICE_CONNECTED,
  DEVICE_MESSAGE_RESPONSE,
  DRAFTING_DECISION,
} from "constants/actionTypes";

import {
  DINI_3590_DRIVER_NAME,
  dini3590ProcessMessage,
  MT_SERIAL_DRIVER_NAME,
  MT_TCP_DRIVER_NAME,
  mtSerialProcessMessage,
  mtTcpProcessMessage,
  PLC_DRIVERS,
  rinstrum5000ProcessMessage,
  RINSTRUM_5000_DRIVER_NAME,
  THOMPSON_LONGHORN_ALLEN_BRADLEY_PLC_DRIVER_NAME,
  TlabAutoDraftPlc,
  tlabAutoDraftPlcProcessMessage,
  WEIGHBRIDGE_DRIVERS,
} from "lib/deviceDrivers";
import {
  MT_HOST_FILE_SESSION_DRIVER_NAME,
  mtHostProcessMessage,
} from "lib/deviceDrivers/mtHost";

import {
  getAutoDraftDecisionCodeByAuctionPenIdLookup,
  getAvailableDevices,
  selectConnectedPLC,
} from "selectors";
import {
  TT_XRS2I_DRIVER_NAME,
  TT_XRS2I_DRIVER_LIFECYCLE,
  truTestXRS2IProcessMessage,
} from "lib/deviceDrivers/truTest/xrs2i";
import { putActionResult } from "./putActionResult";
import { ConnectionStatus } from "constants/scanner";

const DeviceMessageHandler = {
  [MT_SERIAL_DRIVER_NAME]: mtSerialProcessMessage,
  [MT_TCP_DRIVER_NAME]: mtTcpProcessMessage,
  [RINSTRUM_5000_DRIVER_NAME]: rinstrum5000ProcessMessage,
  [DINI_3590_DRIVER_NAME]: dini3590ProcessMessage,
  [THOMPSON_LONGHORN_ALLEN_BRADLEY_PLC_DRIVER_NAME]:
    tlabAutoDraftPlcProcessMessage,
  [MT_HOST_FILE_SESSION_DRIVER_NAME]: mtHostProcessMessage,
  [TT_XRS2I_DRIVER_NAME]: truTestXRS2IProcessMessage,
};

const AutoDraftHandler = {
  [THOMPSON_LONGHORN_ALLEN_BRADLEY_PLC_DRIVER_NAME]: TlabAutoDraftPlc,
};

const DeviceLifecycleHandler = {
  [TT_XRS2I_DRIVER_NAME]: TT_XRS2I_DRIVER_LIFECYCLE,
};

const DEFAULT_DRIVER_TICK_INTERVAL_MS = 1000;
// Track one running tick task per connected device so timer-driven driver actions
// are flushed to Redux without starting duplicate loops for the same connection.
const driverTickTaskByDeviceId = new Map();
// Keep the resolved lifecycle for each connected device so disconnect handling can
// still call driver teardown hooks even when later events omit deviceType metadata.
const activeLifecycleByDeviceId = new Map();

/**
 * Runs a driver-owned tick callback at its requested interval.
 * This ensures timer-based retries/timeouts are emitted even when no new hub message arrives.
 */
function* runDriverTickLoop(deviceId, lifecycle) {
  const tickIntervalMs =
    lifecycle.tickIntervalMs ?? DEFAULT_DRIVER_TICK_INTERVAL_MS;

  while (true) {
    const tickAction = yield call(lifecycle.onTick, deviceId);
    yield* putActionResult(tickAction);
    yield delay(tickIntervalMs);
  }
}

/**
 * Starts a device tick loop only when the driver exposes onTick and no loop exists yet.
 * This avoids duplicate scheduler tasks that would otherwise dispatch repeated actions.
 */
function* ensureDriverTickLoop(deviceId, lifecycle) {
  if (!lifecycle?.onTick || driverTickTaskByDeviceId.has(deviceId)) {
    return;
  }

  const task = yield fork(runDriverTickLoop, deviceId, lifecycle);
  driverTickTaskByDeviceId.set(deviceId, task);
}

/**
 * Stops the active tick loop for one device.
 * Teardown on disconnect prevents orphaned timer callbacks after the device is gone.
 */
function* stopDriverTickLoop(deviceId) {
  const task = driverTickTaskByDeviceId.get(deviceId);
  if (!task) {
    return;
  }

  yield cancel(task);
  driverTickTaskByDeviceId.delete(deviceId);
}

/**
 * Resolves lifecycle hooks for a device using event metadata, tracked state, then store lookup.
 * The fallback order handles hub events that sometimes omit deviceType on reconnect/disconnect paths.
 */
function* resolveLifecycle(deviceId, resolvedDeviceType) {
  if (resolvedDeviceType) {
    return DeviceLifecycleHandler[resolvedDeviceType];
  }

  const trackedLifecycle = activeLifecycleByDeviceId.get(deviceId);
  if (trackedLifecycle) {
    return trackedLifecycle;
  }

  const device = yield getDevice(deviceId);
  return device ? DeviceLifecycleHandler[device.deviceType] : undefined;
}

function* getDevice(deviceId) {
  const state = yield select();

  return getAvailableDevices(state).find(
    device => device.deviceId === deviceId,
  );
}

function* getDeviceMessageHandler(deviceId) {
  const device = yield getDevice(deviceId);
  if (!device) {
    return;
  }

  return DeviceMessageHandler[device.deviceType];
}

function* onDeviceMessage(action) {
  const {
    deviceId,
    object: { type, status, payload },
  } = action;

  const handler = yield getDeviceMessageHandler(deviceId);
  if (!handler) {
    return;
  }

  const device = yield getDevice(deviceId);

  const chainedAction = yield call(handler, device, type, status, payload);
  yield* putActionResult(chainedAction);
}

function* getAutoDraftHandler(deviceId) {
  const device = yield getDevice(deviceId);

  if (!device) {
    return;
  }

  return AutoDraftHandler[device.deviceType].sendDraftingDecision;
}

function* onSendDraftingDecision(action) {
  const { draftingDecision } = action;
  const { draftPenId } = draftingDecision;

  const state = yield select();
  const connectedPLC = selectConnectedPLC(state);
  if (connectedPLC) {
    const { deviceId } = connectedPLC;
    const decision =
      getAutoDraftDecisionCodeByAuctionPenIdLookup(draftPenId)(state);

    const sendDraftingDecisionHandler = yield getAutoDraftHandler(deviceId);

    if (!sendDraftingDecisionHandler) {
      return;
    }

    const chainedAction = sendDraftingDecisionHandler(deviceId, decision);
    if (!chainedAction) {
      return;
    }

    yield put(chainedAction);
  }
}

function* onConnectToScales() {
  const state = yield select();
  yield all(
    getAvailableDevices(state)
      .filter(device => WEIGHBRIDGE_DRIVERS.indexOf(device.deviceType) > -1)
      .map(device => put(connectToDevice(device.deviceId))),
  );
}

function* onConnectToPLC() {
  const state = yield select();
  yield all(
    getAvailableDevices(state)
      .filter(device => PLC_DRIVERS.indexOf(device.deviceType) > -1)
      .map(device => put(connectToDevice(device.deviceId))),
  );
}

function* onDeviceConnected(action) {
  const { deviceId, object } = action;
  const status = object?.device?.status ?? object?.status;
  const deviceType = object?.device?.deviceType ?? object?.deviceType;
  const lifecycle = yield* resolveLifecycle(deviceId, deviceType);
  if (!lifecycle) {
    return;
  }

  if (status === ConnectionStatus.CONNECTED) {
    activeLifecycleByDeviceId.set(deviceId, lifecycle);

    if (lifecycle.onConnected) {
      // Let the driver perform per-connection initialization (for example, protocol sync)
      // so generic saga code does not need device-specific startup logic.
      const connectAction = yield call(lifecycle.onConnected, deviceId);
      yield* putActionResult(connectAction);
    }

    yield* ensureDriverTickLoop(deviceId, lifecycle);
    return;
  }

  // Some hubs report dropouts through DEVICE_CONNECTED with a non-connected status.
  // Tear down the session in that case so timers and buffered driver state do not leak.
  if (status) {
    yield* stopDriverTickLoop(deviceId);
    activeLifecycleByDeviceId.delete(deviceId);
    if (lifecycle.onDisconnected) {
      // Delegate teardown to the driver so session caches and timers are cleaned
      // using the same logic as explicit user-initiated disconnect events.
      yield call(lifecycle.onDisconnected, deviceId);
    }
  }
}

/**
 * Handles explicit disconnect requests by stopping scheduler work and calling driver teardown.
 * This path is shared by all drivers that provide lifecycle hooks, while others no-op safely.
 */
function* onDeviceDisconnected(action) {
  const lifecycle = yield* resolveLifecycle(action.deviceId);
  yield* stopDriverTickLoop(action.deviceId);
  activeLifecycleByDeviceId.delete(action.deviceId);

  if (!lifecycle?.onDisconnected) {
    return;
  }

  yield call(lifecycle.onDisconnected, action.deviceId);
}

function* rootSaga() {
  yield takeEvery(DEVICE_MESSAGE_RESPONSE, onDeviceMessage);
  yield takeEvery(DEVICE_CONNECTED, onDeviceConnected);
  yield takeEvery(DISCONNECT_FROM_DEVICE, onDeviceDisconnected);
  yield takeEvery(CONNECT_TO_SCALES, onConnectToScales);
  yield takeEvery(CONNECT_TO_PLC, onConnectToPLC);
  yield takeEvery(DRAFTING_DECISION.ACTION.REQUEST, onSendDraftingDecision);
}

export default rootSaga;
